import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import { SettingEntry, listSystemSettingsApi, updateSystemSettingApi } from "../api/systemSettingsApi";
import { HOMEPAGE } from "../../home/routes";
import { ADMIN_SISTEMA_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./AdminSistema.css";
import "../../dp/pages/DpEco.css";

const formatValue = (entry: SettingEntry, value: unknown): string => {
  if (value === null || value === undefined || value === "") return "sem valor";
  if (entry.type === "boolean") return value ? "Ligado" : "Desligado";
  if (entry.type === "select") {
    return entry.options?.find((o) => o.value === value)?.label ?? String(value);
  }
  if (entry.type === "date" && typeof value === "string") return value.split("-").reverse().join("/");
  return String(value);
};

/**
 * Ordem das seções no menu: primeiro o que o DP mexe mais. Grupo novo que não
 * estiver aqui entra no fim, na ordem em que o backend devolve.
 */
const GROUP_ORDER = [
  "Ponto",
  "Departamento Pessoal",
  "Escala da loja",
  "Avaliação de loja",
  "Vasilhame RGB",
  "Campanhas",
  "Inteligência artificial",
];

const slug = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

export default function SystemSettingsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ADMIN_SISTEMA_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [params, setParams] = useSearchParams();

  const [entries, setEntries] = useState<SettingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  /** Rascunho dos campos de texto/número, por chave — o toggle salva direto. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listSystemSettingsApi();
      setEntries(data);
      setDrafts({});
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Erro ao carregar as configurações.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  // Agrupa preservando a ordem do backend dentro do grupo; os grupos seguem GROUP_ORDER.
  const groups = useMemo(() => {
    const result: [string, SettingEntry[]][] = [];
    for (const e of entries) {
      const found = result.find(([g]) => g === e.group);
      if (found) found[1].push(e);
      else result.push([e.group, [e]]);
    }
    const rank = (g: string) => {
      const i = GROUP_ORDER.indexOf(g);
      return i < 0 ? GROUP_ORDER.length : i;
    };
    return result.sort(([a], [b]) => rank(a) - rank(b));
  }, [entries]);

  // Uma seção por vez, guardada na URL (?secao=ponto): o Voltar e o link direto funcionam.
  const activeGroup = groups.find(([g]) => slug(g) === params.get("secao"))?.[0] ?? groups[0]?.[0] ?? null;
  const activeItems = groups.find(([g]) => g === activeGroup)?.[1] ?? [];
  const selectGroup = (group: string) => setParams({ secao: slug(group) }, { replace: true });

  // Subtítulos do grupo: itens sem seção ficam no topo.
  const sections = useMemo(() => {
    const result: [string | null, SettingEntry[]][] = [];
    for (const e of activeItems) {
      const name = e.section ?? null;
      const found = result.find(([s]) => s === name);
      if (found) found[1].push(e);
      else result.push([name, [e]]);
    }
    return result;
  }, [activeItems]);

  const changedCount = (items: SettingEntry[]) => items.filter((e) => !e.isDefault).length;

  const save = async (entry: SettingEntry, value: unknown) => {
    setSavingKey(entry.key);
    try {
      const updated = await updateSystemSettingApi(entry.key, value);
      setEntries((prev) => prev.map((e) => (e.key === updated.key ? updated : e)));
      setDrafts((prev) => {
        const { [entry.key]: _removed, ...rest } = prev;
        return rest;
      });
      toast.success("Configuração salva.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao salvar a configuração.");
    } finally {
      setSavingKey(null);
    }
  };

  const saveDraft = (entry: SettingEntry) => {
    const raw = (drafts[entry.key] ?? "").trim();
    if (entry.type === "date" && raw === "") {
      if (entry.nullable) return save(entry, null);
      toast.error("Informe uma data.");
      return;
    }
    if (entry.type === "number") {
      if (raw === "") {
        if (entry.nullable) return save(entry, null);
        toast.error("Informe um valor.");
        return;
      }
      const n = Number(raw.replace(",", "."));
      if (!Number.isFinite(n)) {
        toast.error("Informe um número válido.");
        return;
      }
      return save(entry, n);
    }
    return save(entry, raw);
  };

  const renderControl = (entry: SettingEntry) => {
    const busy = savingKey === entry.key;

    if (entry.type === "boolean") {
      const on = entry.value === true;
      return (
        <label className="admSwitch">
          <input type="checkbox" checked={on} disabled={busy} onChange={() => save(entry, !on)} aria-label={entry.label} />
          <span className="admSwitch__track" aria-hidden="true" />
          <span className="admSwitch__label">{on ? "Ligado" : "Desligado"}</span>
        </label>
      );
    }

    if (entry.type === "select") {
      return (
        <select
          className="ecoSelect"
          value={String(entry.value ?? "")}
          disabled={busy}
          onChange={(e) => save(entry, e.target.value)}
          aria-label={entry.label}
        >
          {(entry.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    const current = entry.value === null || entry.value === undefined ? "" : String(entry.value);
    const draft = drafts[entry.key] ?? current;
    const dirty = draft.trim() !== current;

    return (
      <form
        className="admSettingInput"
        onSubmit={(e) => {
          e.preventDefault();
          saveDraft(entry);
        }}
      >
        <input
          className="ecoInput"
          type={entry.type === "number" ? "number" : entry.type === "date" ? "date" : "text"}
          inputMode={entry.type === "number" ? (entry.integer ? "numeric" : "decimal") : undefined}
          min={entry.min}
          max={entry.max}
          step={entry.type === "number" ? (entry.integer ? 1 : "any") : undefined}
          placeholder={entry.nullable ? "sem valor" : undefined}
          value={draft}
          disabled={busy}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [entry.key]: e.target.value }))}
          aria-label={entry.label}
        />
        <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy || !dirty}>
          {busy ? "Salvando..." : "Salvar"}
        </button>
        {entry.nullable && current !== "" ? (
          <button type="button" className="ecoBtn" disabled={busy} onClick={() => save(entry, null)}>
            Limpar
          </button>
        ) : null}
      </form>
    );
  };

  if (!token) return null;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <div className="admSettingsPage">
          <EcoPageHeader
            crumbs={[
              { label: "Início", onClick: () => navigate(HOMEPAGE()) },
              {
                label: "Administração do Sistema",
                onClick: () => navigate(ADMIN_SISTEMA_PAGE()),
              },
              { label: "Configurações" },
            ]}
            title="Configurações do sistema"
            subtitle="Defina como a aplicação se comporta: metas, obrigatoriedades e módulos opcionais."
            onBack={goBack}
            backLabel="Voltar para a Administração"
          />

          {loading ? (
            <p className="ecoEmpty">Carregando...</p>
          ) : loadError ? (
            <div className="ecoCard admSettingsError">
              <p>{loadError}</p>
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={load}>
                Tentar novamente
              </button>
            </div>
          ) : groups.length === 0 ? (
            <p className="ecoEmpty">Nenhuma configuração disponível.</p>
          ) : (
            <div className="admSettingsLayout">
              <nav className="admSettingsNav" aria-label="Seções das configurações">
                {groups.map(([group, items]) => {
                  const changed = changedCount(items);
                  return (
                    <button
                      key={group}
                      type="button"
                      className={`admSettingsNav__item${group === activeGroup ? " admSettingsNav__item--active" : ""}`}
                      aria-current={group === activeGroup ? "page" : undefined}
                      onClick={() => selectGroup(group)}
                    >
                      <span>{group}</span>
                      <span className="admSettingsNav__count" title={changed ? `${changed} alterada(s) do padrão` : undefined}>
                        {items.length}
                      </span>
                    </button>
                  );
                })}
              </nav>

              <section className="ecoCard admSettingsGroup">
                <h2 className="admSettingsGroup__title">{activeGroup}</h2>
                {sections.map(([section, items]) => (
                  <div key={section ?? "_"} className="admSettingsSection">
                    {section ? <h3 className="admSettingsSection__title">{section}</h3> : null}
                    <div className="admSettingList">
                      {items.map((entry) => (
                        <div key={entry.key} className="admSetting">
                          <div className="admSetting__text">
                            <div className="admSetting__label">{entry.label}</div>
                            <div className="admSetting__desc">{entry.description}</div>
                            <div className="admSetting__meta">
                              Padrão: {formatValue(entry, entry.default)}
                              {!entry.isDefault && entry.updatedAt ? (
                                <>
                                  {" · "}Alterado
                                  {entry.updatedBy ? ` por ${entry.updatedBy.nomeCompleto}` : ""} em {formatDateTime(entry.updatedAt)}
                                </>
                              ) : null}
                              {!entry.isDefault ? (
                                <>
                                  {" · "}
                                  <button
                                    type="button"
                                    className="ecoLinkBtn admSetting__restore"
                                    disabled={savingKey === entry.key}
                                    onClick={() => save(entry, entry.default)}
                                  >
                                    Restaurar padrão
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </div>
                          <div className="admSetting__control">{renderControl(entry)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
